import { MouseEvent as MouseEventReact, PropsWithChildren, useCallback, useEffect, useRef, useState } from "react";
import classes from './dropDownList.module.scss'
import { LoaderSpinner } from "../../spinner";
import { List } from "../list/List";
import arrowDown from '../../../lib/assets/arrowDown.png'
import { IItem } from "../../../model/types";
import { MyInput } from "../../input";
import { MyCheckbox } from "../../myCheckbox";
import { useSearchItems } from "../../../lib/hooks/useSearchItems";

interface IProps<TId extends string | number> {
    items: IItem<TId>[];
    selectedIdItems: TId[];
    onSelected: (item: IItem<TId>) => (selected: boolean) => void
    onSelectAll?: (selectedIds: TId[]) => void;
    isLoading: boolean;
    selectedCount?: boolean;
    plainSelected?: boolean;
    placeholder?: string;
    search?: boolean;
    searchPlaceholder?: string;
    emptyText?: string;
    disabled?: boolean;
}

export const DropDownListSelected = <TId extends string | number> ({
    selectedIdItems, items, isLoading, onSelected, onSelectAll, selectedCount, plainSelected, placeholder, 
    search = false, searchPlaceholder = 'Поиск...', emptyText, disabled = false, children,
} : PropsWithChildren<IProps<TId>>) => {
    
    const [open, setOpen] = useState<boolean>(false)
    const [searchValue, setSearchValue] = useState<string>('')
    const refWrapper = useRef<HTMLDivElement>(null)
    const filteredItems = useSearchItems(items, searchValue)
    const isAllSelected = Boolean(filteredItems.length) &&
        filteredItems.every(item => selectedIdItems.includes(item.id))

    const clickDoc = useCallback((e: MouseEvent) => {
        const target = e.target as Element;
        if((refWrapper.current && refWrapper.current.contains(target)) || target.closest('.modal')){}
        else{
            setOpen(false)
            document.removeEventListener('click', clickDoc, {capture: true})
        }
    }, [])

    useEffect(() => {
        return () => {
            document.removeEventListener('click', clickDoc, {capture: true})
        }
    }, [clickDoc])

    useEffect(() => {
        if (disabled) setOpen(false)
    }, [disabled])

    const onOpen = (e: MouseEventReact) => {
        const target = e.target as HTMLElement;
        if(!isLoading && !disabled && !target.closest('.' + classes.item)){
            if(open){
                setOpen(false)
                document.removeEventListener('click', clickDoc, {capture: true})
            }
            else{
                document.addEventListener('click', clickDoc, {capture: true})
                setOpen(true)
            }
        }
    }

    const getSelectedNames = () => {
        const names: IItem<TId>[] = [];
        for(let i = 0; i < selectedIdItems.length; i++){
            const target = items.find(item => item.id === selectedIdItems[i])
            if(target){
                names.push(target)
            }
        }
        return names
    }

    const onToggleSelectAll = (selected: boolean) => {
        if(!onSelectAll) return

        const filteredIds = new Set(filteredItems.map(item => item.id))
        const nextSelectedIds = selected
            ? [
                ...selectedIdItems,
                ...filteredItems
                    .map(item => item.id)
                    .filter(id => !selectedIdItems.includes(id)),
            ]
            : selectedIdItems.filter(id => !filteredIds.has(id))

        onSelectAll(nextSelectedIds)
    }

    return (
        <section className={classes.wrapper} ref={refWrapper}>
            <section
                onClick={onOpen} 
                className={classes.selectedWrap + (disabled ? ` ${classes.disabled}` : '')}
                onMouseDown={e => e.preventDefault()}
            >
                {
                    isLoading
                        ?
                    <section className={classes.loader}><LoaderSpinner /></section>
                        :
                    <>
                        <section className={classes.selected}>
                            {
                                selectedIdItems.length === 0
                                    ?
                                <section className={classes.placeholder}>
                                    {placeholder || 'Выберите значение'}
                                </section>
                                    :
                                selectedCount
                                    ?
                                <>Выбрано: {selectedIdItems.length}</>
                                    :
                                plainSelected
                                    ?
                                getSelectedNames().map(item =>
                                    <section
                                        className={classes.plainItem}
                                        key={item.id}
                                    >
                                        {item.name}
                                    </section>
                                )
                                    :
                                getSelectedNames().map(item => 
                                    <section 
                                        className={classes.item} 
                                        key={item.id}
                                    >
                                        {item.name}
                                        <svg 
                                            className={classes.delete } 
                                            onClick={() => onSelected(item)(false)} 
                                            width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path d="M25 7L7 25" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                            <path d="M25 25L7 7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                    </section>
                                )
                            }
                        </section>
                        <img className={(open ? ` ${classes.open}` : '')} src={arrowDown} alt="" />
                    </>
                }
            </section>
            {
                open && !disabled
                    &&
                <section className={classes.container}>
                    {
                        search 
                            &&
                        <MyInput
                            placeholder={searchPlaceholder}
                            value={searchValue}
                            setValue={setSearchValue}
                        />
                    }
                    {children}
                    {
                        onSelectAll && Boolean(filteredItems.length)
                            &&
                        <section className={classes.selectAll}>
                            <MyCheckbox
                                checked={isAllSelected}
                                onSelected={onToggleSelectAll}
                                label={"Выбрать все"}
                            />
                        </section>
                    }
                    <section className={classes.list + (children || search ? ` ${classes.feature}` : '')}>
                        <List
                            onSelected={onSelected}
                            items={filteredItems} 
                            selectedIdItems={selectedIdItems} 
                            emptyText={emptyText}
                        />
                    </section>
                </section>
            }
        </section>
    )
}
